<div class="image-search-panel">

  <!-- Query + source picker -->
  <div class="search-bar">
    <div class="flex gap-2 items-start">
      <div class="flex-1">
        <ui-input
          type="search"
          [placeholder]="placeholder"
          [value]="query"
          [fullWidth]="true"
          [clearable]="true"
          ariaLabel="Image search query"
          (valueChange)="onQueryChange($event)"
          (keydown.enter)="submitSearch()"/>
      </div>
      <ui-button
        variant="primary"
        size="md"
        [loading]="isSearching"
        [disabled]="isSearching"
        (clicked)="submitSearch()">
        <fa-icon [icon]="faSearch" class="mr-2"></fa-icon>
        Search
      </ui-button>
      <ui-button
        variant="ghost"
        size="md"
        (clicked)="cancel()">
        <fa-icon [icon]="faBack" class="mr-2"></fa-icon>
        Back
      </ui-button>
    </div>

    @if (sources.length > 1) {
      <div class="source-picker">
        <span class="source-label">Source</span>
        @for (source of sources; track source.key) {
          <button
            type="button"
            class="source-chip"
            [class.active]="pinnedProvider === source.key"
            [class.answering]="!pinnedProvider && answeringProvider === source.key"
            [title]="pinnedProvider === source.key ? 'Tap to search all sources again' : 'Search only ' + source.name"
            (click)="toggleProvider(source.key)">
            {{ source.name }}
          </button>
        }
        @if (!pinnedProvider && answeringProvider) {
          <span class="source-hint">showing {{ providerName(answeringProvider) }}</span>
        }
      </div>
    }
  </div>

  <!-- Search unavailable: no provider configured for this subject -->
  @if (unavailable) {
    <div class="state-panel">
      <fa-icon [icon]="faWarning" class="state-icon"></fa-icon>
      <h5>Image search is not available</h5>
      <p>No image provider is configured for {{ subject }}s on this server.</p>
    </div>
  }

  <!-- Hard error -->
  @if (!unavailable && errorMessage) {
    <div class="alert alert-error py-2 px-3 my-3" role="alert">
      <small>
        <fa-icon [icon]="faWarning" class="mr-1"></fa-icon>
        {{ errorMessage }}
      </small>
    </div>
  }

  <!-- Loading first page -->
  @if (isSearching) {
    <div class="state-panel">
      <fa-icon [icon]="faSpinner" [animation]="'spin'" class="state-icon"></fa-icon>
      <h5>Searching…</h5>
    </div>
  }

  <!-- No hits -->
  @if (!isSearching && !unavailable && !errorMessage && hasSearched && hits.length === 0) {
    <div class="state-panel">
      <fa-icon [icon]="faSearch" class="state-icon"></fa-icon>
      <h5>Nothing found for “{{ effectiveQuery || query }}”</h5>
      <p>Try a shorter or more general search term.</p>
    </div>
  }

  <!-- Results -->
  @if (hits.length > 0) {
    <div class="result-grid">
      @for (view of hits; track view.hit.candidate_id) {
        <div
          class="result-item"
          [class.selected]="selected?.hit?.candidate_id === view.hit.candidate_id"
          [class.failed]="view.failed"
          [attr.aria-selected]="selected?.hit?.candidate_id === view.hit.candidate_id"
          (click)="selectHit(view)">

          @if (view.objectUrl) {
            <img [src]="view.objectUrl" class="result-image" [alt]="view.hit.title || 'Search result'"/>
          } @else if (view.failed) {
            <div class="result-placeholder">
              <fa-icon [icon]="faWarning"></fa-icon>
            </div>
          } @else {
            <div class="result-placeholder">
              <fa-icon [icon]="faSpinner" [animation]="'spin'"></fa-icon>
            </div>
          }

          @if (selected?.hit?.candidate_id === view.hit.candidate_id) {
            <div class="selected-check">
              <fa-icon [icon]="faCheck"></fa-icon>
            </div>
          }

          <!-- Credit: mandatory for providers whose licence requires it -->
          <div class="result-credit">
            <span class="credit-provider">{{ providerName(view.hit.provider) }}</span>
            @if (view.hit.attribution?.author) {
              <span class="credit-author">· {{ view.hit.attribution.author }}</span>
            }
          </div>
        </div>
      }
    </div>

    @if (hasMore) {
      <div class="mt-3 flex justify-center">
        <ui-button
          variant="ghost"
          size="sm"
          [loading]="isLoadingMore"
          [disabled]="isLoadingMore"
          (clicked)="loadMore()">
          Load more
        </ui-button>
      </div>
    }
  }

  <!-- Action bar for the picked image -->
  @if (selected) {
    <div class="action-bar">
      <div class="action-meta">
        @if (selected.hit.title) {
          <span class="action-title">{{ selected.hit.title }}</span>
        }
        <span class="action-attribution">
          via {{ providerName(selected.hit.provider) }}
          @if (selected.hit.attribution?.author) {
            · {{ selected.hit.attribution.author }}
          }
          @if (selected.hit.attribution?.license) {
            · {{ selected.hit.attribution.license }}
          }
          @if (selected.hit.attribution?.source_url) {
            ·
            <a [href]="selected.hit.attribution.source_url" target="_blank" rel="noopener noreferrer">
              source
            </a>
          }
        </span>
      </div>
      <div class="action-buttons">
        <ui-button
          variant="secondary"
          size="sm"
          [disabled]="!canImport"
          (clicked)="importSelected(false)">
          Add without using it
        </ui-button>
        <ui-button
          variant="primary"
          size="sm"
          [loading]="isImporting"
          [disabled]="!canImport"
          (clicked)="importSelected(true)">
          <fa-icon [icon]="faCheck" class="mr-1"></fa-icon>
          {{ primaryActionLabel }}
        </ui-button>
      </div>
    </div>
  }
</div>
